import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Undo2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import Step1Form from './details/Step1';
import Step2Form from './details/Step2';
import Step3Form from './details/Step3';
import { showToast } from '../../components/ToastConfig';
import { useAuth } from '../../contexts/AuthContext';

import NewEventService from '../../services/event-service';
import NewEventDayService from '../../services/event-day-service';
import NewEventAgendaService from '../../services/event-agenda-service';

const PHOTO_BASIC = process.env.REACT_APP_FILE_BASIC;

const EventDetails = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [mode, setMode] = useState('create');
  const [eventData, setEventData] = useState(null);
  const { id: urlId } = useParams();
  const [id, setId] = useState(urlId);
  const { logout } = useAuth();
  const [isValidatingOrSaving, setIsValidatingOrSaving] = useState(false);

  useEffect(() => {
    console.log("urlId useEffect: ", urlId);
    setId(urlId);
  }, [urlId]);
  
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    trigger,
    watch,
    setValue,
    getValues,
    reset
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      isEnabled: "",
      listImageUrl: "",
      headerImageUrl: "",
      name: "",
      startAt: "",
      endAt: "",
      utc: "",
      venue: "",
      location: "",
      intro: "",
      about: "",
      eventDays: [
        { 
          date: "", 
          agendas: [{ timeRange: "", programme: "", speakers: "" }]
        }
      ]
    }
  });

  useEffect(() => {
    // This would fetch event data if editing an existing event
    const fetchEventData = async () => {
      if (!id || id === "new") {
        setMode('create');
        return;
      }
      
      try {
        setMode('edit');

        const res = await NewEventService.getDetails(id);
        console.log(res);
        setEventData(res?.data);
        
        // Set form values from fetched data
        Object.keys(res?.data).forEach(key => {
            if (key === 'isEnabled') {
              setValue(key, res?.data[key] ? "true" : "false");
            } else if (key === 'startAt' || key === 'endAt') {
                setValue(key, res?.data[key]);
            } else if (key === 'listImageUrl' || key === 'headerImageUrl') {
                if (res?.data[key]) {
                    if (res?.data[key]?.startsWith(PHOTO_BASIC)) {
                        setValue(key, res?.data[key]);
                      } else {
                        setValue(key, `${PHOTO_BASIC}${res?.data[key]}`);
                      }      
                } else {
                    setValue(key, null);
                }
            } else if (key === "eventDays") {
                if (res?.data.eventDays && res?.data.eventDays.length > 0) {
                    // 將服務器返回的 eventDays 資料轉換為表單需要的格式
                    const formattedEventDays = res.data.eventDays.map(day => {
                    // 將每個 day 的 name 字段映射到 date 字段
                        return {
                            id: day.id,
                            date: day.name, // Step3 中使用 date 作為日期字段
                            agendas: day.agendas.map(agenda => {
                            // 保留所有 agenda 字段，確保有 id
                            return {
                                id: agenda.id,
                                timeRange: agenda.timeRange,
                                programme: agenda.programme,
                                speakers: agenda.speakers
                            };
                            })
                        };
                    });
                        
                    // 設置格式化後的 eventDays 到表單
                    setValue('eventDays', formattedEventDays);
                }
            } else {
              setValue(key, res?.data[key]);
            }
        });
      } catch (error) {
        console.error('Failed to fetch event data:', error);
        showToast.error('獲取活動資料失敗，請稍後再試');
      }
    };

    fetchEventData();
  }, [id, setValue]);

  const saveStepData = async (stepData, isFullSubmit = false) => {
    console.log(getValues('isEnabled'));
    const allValues = getValues();
    try {
      // 對數據進行必要的轉換
      let transformedData = {
        ...allValues,
        ...stepData,
        isEnabled: getValues('isEnabled') === "true", // 將字符串轉為布爾值
      };
      
      delete transformedData.id;
      console.log(`Saving data:`, transformedData);
      
      // 這裡應該調用你的 API 服務來保存數據
      // 例如：
      if (mode === 'edit') {
        const res = await NewEventService.update(id, transformedData);
        console.log(res);
        return { success: true, data: res.data };
      } else {
        // 如果是新建事件，你可能需要先創建然後更新
        if (!id || id === "new") {
          const res = await NewEventService.create(transformedData);
          console.log(res);
          // 在不重新加載頁面的情況下更新 URL 中的事件 ID
          window.history.replaceState(null, '', `/#/event/${res.data}`);
          // 設置 id 以供後續更新使用
          setId(res.data);
          setMode('edit');
          return { success: true, data: res.data };
        } else {
          const res = await NewEventService.update(id, transformedData);
          return { success: true, data: res.data };
        }
      }
    } catch (error) {
      console.error('儲存資料失敗:', error);
      showToast.error('儲存失敗，請稍後再試');
      return { success: false, error };
    }
  };

  // 驗證第一步並儲存資料
  const validateAndSaveStep1 = async () => {
    setIsValidatingOrSaving(true);
    try {
      // 驗證必填欄位
      const isValid = await trigger(['name', 'isEnabled']);
      
      if (!isValid) {
        // 若驗證不通過，顯示錯誤訊息
        if (errors.name) {
          showToast.error(errors.name.message || '請填寫活動名稱');
        } else if (errors.isEnabled) {
          showToast.error(errors.isEnabled.message || '請選擇是否啟用');
        } else {
          showToast.error('請確認所有必填欄位填寫正確');
        }
        return false;
      }
      
      // 獲取第一步資料
      const step1Data = {
        name: getValues('name'),
        isEnabled: getValues('isEnabled'),
        startAt: getValues('startAt'),
        endAt: getValues('endAt'),
        utc: getValues('utc') ? Number(getValues('utc')) : 0,
        venue: getValues('venue'),
        location: getValues('location')
      };
      
      // 儲存第一步資料
      const saveResult = await saveStepData(step1Data);
      
      if (saveResult.success) {
        if (id === "new" && saveResult.data) {
          // 如果是新建事件，更新 ID 並修改 URL
          setId(saveResult.data);
          window.history.replaceState(null, '', `/#/event/${saveResult.data}`);
        }
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('驗證或儲存第一步資料失敗:', error);
      showToast.error('處理失敗，請稍後再試');
      return false;
    } finally {
      setIsValidatingOrSaving(false);
    }
  };

  // 處理點擊步驟條的事件
  const handleStepClick = async (step) => {
    // 如果要前往的步驟與當前步驟相同，則不做任何操作
    if (step === currentStep) return;
    
    // 如果要前往步驟 2 或 3，先驗證並儲存第一步資料
    if (step > 1 && currentStep === 1) {
      const isStepValidAndSaved = await validateAndSaveStep1();
      if (!isStepValidAndSaved) {
        // 驗證或儲存失敗，保持在第一步
        return;
      }
    }
    
    // 如果是從步驟 3 返回步驟 2，或從步驟 2 返回步驟 1，直接切換
    if (step < currentStep) {
      setCurrentStep(step);
      window.scrollTo(0, 0);
      return;
    }
    
    // 如果是從步驟 2 前往步驟 3，不需要額外驗證，直接切換
    if (currentStep === 2 && step === 3) {
      setCurrentStep(3);
      window.scrollTo(0, 0);
      return;
    }
    
    // 設置當前步驟
    setCurrentStep(step);
    window.scrollTo(0, 0);
  };

  const handleNext = async () => {
    // Check for errors in the current step
    const stepErrors = Object.keys(errors);
    
    if (stepErrors.length > 0) {
      showToast.error('請修正錯誤後再繼續');
      return;
    }

    // Validate required fields for each step
    if (currentStep === 1) {
      const isStepValidAndSaved = await validateAndSaveStep1();
      if (isStepValidAndSaved) {
        setCurrentStep(2);
        window.scrollTo(0, 0);
      }
    } else if (currentStep === 2) {
      // Get step 2 fields
      const step2Data = {
        listImageUrl: getValues('listImageUrl'),
        headerImageUrl: getValues('headerImageUrl'),
        intro: getValues('intro'),
        about: getValues('about')
      };

      console.log(step2Data)
      
      // Save step 2 data to backend
      const saveResult = await saveStepData(step2Data);
      console.log(saveResult);

      if (saveResult.success) {
        setCurrentStep(3);
        window.scrollTo(0, 0);
      }
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

const callApiWithRetry = async (apiFunc, params, maxRetries = 5) => {
    let retries = 0;
    
    while (retries < maxRetries) {
      try {
        const response = await apiFunc(params);
        return response; // 成功時返回響應
      } catch (error) {
        retries++;
        console.log(`API 呼叫失敗，嘗試重試 (${retries}/${maxRetries})`, error);
        
        if (retries >= maxRetries) {
          throw new Error(`達到最大重試次數 (${maxRetries})，API 呼叫失敗`);
        }
        
        // 指數退避延遲，從 1 秒開始，每次重試翻倍（最多 30 秒）
        const delay = Math.min(1000 * Math.pow(2, retries - 1), 30000);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  };
  
  const validateEventDays = (eventDays) => {
    if (!eventDays || eventDays.length === 0) {
      return { isValid: true, validEventDays: [] };
    }
  
    // Create a copy to avoid mutating the original array
    const daysToValidate = [...eventDays];
    const validEventDays = [];
    
    // Validate each day and its agendas
    for (let i = 0; i < daysToValidate.length; i++) {
      const day = daysToValidate[i];
      const dayDate = day.date?.trim() || "";
      
      // Skip completely empty day entries
      if (!dayDate && (!day.agendas || day.agendas.every(agenda => 
        !agenda.timeRange?.trim() && !agenda.programme?.trim() && !agenda.speakers?.trim()
      ))) {
        continue;
      }
      
      // Check if date is provided when agendas exist
      if (!dayDate) {
        showToast.error(`請填寫第 ${i+1} 天的 name`);
        return { isValid: false, validEventDays: [] };
      }
      
      // Validate agendas
      const validAgendas = [];
      for (let j = 0; j < day.agendas.length; j++) {
        const agenda = day.agendas[j];
        const timeRange = agenda.timeRange?.trim() || "";
        const programme = agenda.programme?.trim() || "";
        const speakers = agenda.speakers?.trim() || "";
        
        // Skip empty agendas
        if (!timeRange && !programme && !speakers) {
          continue;
        }
        
        // Validate required fields
        if (!timeRange) {
          showToast.error(`請填寫 ${dayDate} 的 timeRange`);
          return { isValid: false, validEventDays: [] };
        }
        
        if (!programme) {
          showToast.error(`請填寫 ${dayDate} 的 programme`);
          return { isValid: false, validEventDays: [] };
        }
        
        if (!speakers) {
          showToast.error(`請填寫 ${dayDate} 的 speakers`);
          return { isValid: false, validEventDays: [] };
        }
        
        validAgendas.push(agenda);
      }
      
      // If date has value but no valid agendas
      if (dayDate && validAgendas.length === 0) {
        showToast.error(`請確保 ${dayDate} 的 agendas 欄位填寫完整`);
        return { isValid: false, validEventDays: [] };
      }
      
      // Add valid day with filtered agendas
      validEventDays.push({
        ...day,
        agendas: validAgendas
      });
    }
    
    return { 
      isValid: true, 
      validEventDays 
    };
  };
  

  // Replace the Promise.all approach with sequential processing
  const onSubmit = async () => {
    const allValues = getValues();
    console.log("allValues:", allValues);
  
    // Check first page validation when not on first page
    if (!allValues?.name?.trim() || !allValues?.isEnabled) {
      setCurrentStep(1);
      if (!allValues?.name?.trim()) {
        showToast.error('請輸入活動名稱');
      } else if (!allValues?.isEnabled) {
        showToast.error('請選擇是否啟用');
      }
      return;
    }
  
    // Validate event days
    let eventDaysData = getValues('eventDays');
    if (eventDaysData?.length > 0) {
      const { isValid, validEventDays } = validateEventDays(eventDaysData);
      if (!isValid) {
        console.log("is invalid");
        setCurrentStep(3);
        return;
      }
    }
    
    // Get validated event days
    eventDaysData = getValues('eventDays');
    console.log("eventDaysData:", eventDaysData);

    let newDatabaseId;
  
    // Create a new event if needed
    if (!id || id === "new") {
      const requestBody = {
        ...allValues,
        isEnabled: getValues('isEnabled') === "true",
        utc: getValues('utc') ? Number(getValues('utc')) : 0,
      };
      const res = await NewEventService.create(requestBody);
      console.log("res: ", res);
      // Update URL without page reload
      window.history.replaceState(null, '', `/#/event/${res.data}`);
      // id = res.data;
      newDatabaseId = res.data;
      console.log("newDatabaseId: ", newDatabaseId);
      setMode('edit');
    }
  
    // Filter and prepare event days data
    const eventDaysTransformedData = eventDaysData.map((i) => {
      return {
        agendas: i.agendas.filter(j => j.timeRange || j.programme || j.speakers),
        date: i?.date,
        id: i?.id
      }
    });
    console.log("eventDaysTransformedData:", eventDaysTransformedData);
  
    try {
      // Update event main data
      const requestBody = {
        ...allValues,
        isEnabled: getValues('isEnabled') === "true",
        utc: getValues('utc') ? Number(getValues('utc')) : 0,
      };
      delete requestBody.id;
      const res = await NewEventService.update(id ? id : newDatabaseId, requestBody);
      console.log("res: ", res);
  
      // Process event days sequentially to maintain order
      for (let i = 0; i < eventDaysTransformedData.length; i++) {
        const dayData = eventDaysTransformedData[i];
        if (!dayData?.date?.trim()) continue;
        
        const daysObj = {
          "eventId": id,
          "name": dayData.date
        };
        console.log("daysObj:", daysObj);
        
        const isNewDay = !dayData.id;
        console.log("isNewDay:", isNewDay);
        
        try {
          // Update or create the day
          let dayResult;
          if (isNewDay) {
            dayResult = await NewEventDayService.create(daysObj);
          } else {
            dayResult = await NewEventDayService.update(dayData.id, daysObj);
          }
          console.log(`successfully processed date. data: `, dayResult);
          
          const dayId = isNewDay ? dayResult?.data : dayData.id;
          
          // Process agendas for this day sequentially to maintain order
          for (let j = 0; j < dayData.agendas.length; j++) {
            const agenda = dayData.agendas[j];
            const isNewAgenda = !agenda.id;
            console.log("isNewAgenda: ", isNewAgenda);
            
            const agendasObj = {
              "eventDayId": dayId,
              timeRange: agenda.timeRange,
              programme: agenda.programme,
              speakers: agenda.speakers
            };
            console.log("agendasObj:", agendasObj);
            
            let agendaResult;
            if (isNewAgenda) {
              agendaResult = await NewEventAgendaService.create(agendasObj);
            } else {
              agendaResult = await NewEventAgendaService.update(agenda.id, agendasObj);
            }
            console.log(`successfully processed agenda. data: `, agendaResult);
          }
        } catch (error) {
          console.error(`Error processing day ${dayData.date}:`, error);
          throw error;
        }
      }
      
      showToast.success('更新成功');
      setTimeout(() => {
        navigate(`/event`);
      }, 1000);
      
    } catch (error) {
      console.error('處理 eventDays 時發生錯誤', error);
      showToast.error('更新失敗，請稍後再試');
    }
  };

  return (
    <div className="container mx-auto p-6">
      <Link to="/event" className="text-blue-500 hover:text-blue-600 group flex items-center mb-5 w-fit p-3 -ml-3">
        <Undo2 size={36} className='text-blue-500 group-hover:text-blue-600 mr-1' />  
        <span className='font-bold'>返回</span>
      </Link>
      
      <div className="mb-8">
        <div className="flex justify-between items-center relative">
          {[1, 2, 3].map((step) => (
            <div
              key={step}
              className={`w-8 h-8 rounded-full flex items-center justify-center cursor-pointer ${
                currentStep >= step ? 'bg-blue-500 text-white' : 'bg-gray-200'
              } ${isValidatingOrSaving ? 'pointer-events-none opacity-70' : ''}`}
              onClick={() => handleStepClick(step)}
            >
              {step}
            </div>
          ))}
          <div className='absolute top-1/2 -translate-y-full w-full h-[1px] bg-gray-200 -z-10'></div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {currentStep === 1 && (
          <Step1Form 
            register={register} 
            errors={errors} 
            watch={watch}
            setValue={setValue}
          />
        )}
        
        {currentStep === 2 && (
          <Step2Form 
            register={register} 
            errors={errors} 
            watch={watch}
            setValue={setValue}
            control={control}
            eventId={id}
          />
        )}
        
        {currentStep === 3 && (
          <Step3Form 
            register={register} 
            errors={errors} 
            setValue={setValue} 
            watch={watch} 
            getValues={getValues}
          />
        )}

        <div className={`flex ${currentStep === 1 ? 'justify-end' : 'justify-between'} mt-8`}>
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrev}
              className="px-4 py-2 border border-gray-600 text-gray-600 rounded hover:bg-gray-50"
              disabled={isValidatingOrSaving}
            >
              上一頁
            </button>
          )}
          
          <div className='flex items-center'>
            {currentStep < 3 && (
              <button
                type="button"
                onClick={handleNext}
                className="px-4 py-2 border border-blue-500 text-blue-500 rounded hover:bg-blue-50"
                disabled={isValidatingOrSaving}
              >
                {isValidatingOrSaving ? "處理中..." : "繼續填寫"}
              </button>
            )}
          
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 ml-4"
              disabled={isSubmitting || isValidatingOrSaving}
            >
              {isSubmitting || isValidatingOrSaving ? "提交中..." : "提交表單"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EventDetails;